import React, { useState } from "react";
import {
    Box,
    Grid,
    TextField,
    MenuItem,
    Typography,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import dayjs from "dayjs";
import { tokens } from "../../assets/theme";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useMachines } from "../../hooks/useWeekSchedule";
import { useCalendarOverview } from "../../hooks/useProductionCalendar";
import PlannedCalendarGrid from "./components/PlannedCalendarGrid";
import messages from "./components/messages";
import { FormattedMessage, useIntl } from "react-intl";

const PlannedCalendarIndex = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { formatMessage } = useIntl();

    const [companyKey] = useLocalStorage("companyKey", 1);

    // default: aktuell vecka (måndag–söndag)
    const defaultFrom = dayjs().startOf("week").add(1, "day").format("YYYY-MM-DD");
    const defaultTo = dayjs().startOf("week").add(7, "day").format("YYYY-MM-DD");

    const [fromDate, setFromDate] = useState(defaultFrom);
    const [toDate, setToDate] = useState(defaultTo);
    const [selectedMachineId, setSelectedMachineId] = useState("");

    const { data: machines = [] } = useMachines(companyKey);

    const sourceLabels = {
        0: formatMessage(messages.schema),
        1: formatMessage(messages.manuallyOverridden),
        2: formatMessage(messages.holiday)
    };

    const overviewQuery = useCalendarOverview(
        companyKey,
        fromDate,
        toDate,
        selectedMachineId ? Number(selectedMachineId) : null
    );

    const workspaceBg =
        theme.palette.mode === "dark" ? colors.primary[700] : colors.grey[100];

    const dateTimeIconSx =
        theme.palette.mode === "dark"
            ? {
                  "& input::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                  },
              }
            : {};

    // Dialog state
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const handleOpenDetails = (row) => {
        setSelectedRow(row);
        setDetailsOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedRow(null);
    };

    const rows = overviewQuery.data || [];

    return (
        <Box
            sx={{
                p: 2,
                backgroundColor: workspaceBg,
                display: "flex",
                flexDirection: "column",
                gap: 1,
            }}
        >
            <Typography variant="h5" gutterBottom>
                <FormattedMessage {...messages.plannedCalendar} />
            </Typography>

            {/* Filterrad */}
            <Grid container spacing={2} mb={1}>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        type="date"
                        label={formatMessage(messages.fromDate)}
                        InputLabelProps={{ shrink: true }}
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        sx={dateTimeIconSx}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        type="date"
                        label={formatMessage(messages.toDate)}
                        InputLabelProps={{ shrink: true }}
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        sx={dateTimeIconSx}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        select
                        fullWidth
                        label={formatMessage(messages.machines)}
                        value={selectedMachineId}
                        onChange={(e) => setSelectedMachineId(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>{formatMessage(messages.allMachines)}</em>
                        </MenuItem>
                        {machines.map((m) => (
                            <MenuItem key={m.id} value={m.id}>
                                {m.machineName}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>

            {/* DataGridPro istället för Table */}
            {rows.length === 0 && !overviewQuery.isLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <FormattedMessage {...messages.noPlannedCalendarFound} />
                </Typography>
            ) : (
                <PlannedCalendarGrid
                    rows={rows}
                    isLoading={overviewQuery.isLoading}
                    onShowDetails={handleOpenDetails}
                />
            )}

            {/* Detaljdialog – enkel första version */}
            <Dialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: colors.primary[500],
                        color: "#ffffff",             
                        border: `1px solid ${colors.primary[600]}`,
                    },
                }}
            >
                <DialogTitle><FormattedMessage {...messages.detailsForPlannedCalendar} /></DialogTitle>
                <DialogContent dividers>
                    {selectedRow ? (
                        <Box>
                            <Typography gutterBottom>
                                <strong><FormattedMessage {...messages.date} />:</strong>{" "}
                                {dayjs(selectedRow.date).format("YYYY-MM-DD")}
                            </Typography>
                            <Typography gutterBottom>
                                <strong><FormattedMessage {...messages.plannedHours} />:</strong>{" "}
                                {selectedRow.plannedProductionHours}
                            </Typography>
                            <Typography gutterBottom>
                                <strong><FormattedMessage {...messages.source} />:</strong>{" "}
                                {sourceLabels[selectedRow.source] ?? selectedRow.source}
                            </Typography>
                            <Typography gutterBottom>
                                <strong><FormattedMessage {...messages.assignedMachines} /> ({selectedRow.machineCount}):</strong>
                                <br />
                                {selectedRow.machineNames}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography><FormattedMessage {...messages.noRowSelected} /></Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseDetails}
                        variant="contained"
                        >
                            <FormattedMessage {...messages.close} />
                            </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PlannedCalendarIndex;